import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowUpRight, Clock, User } from "lucide-react";
import axios from "axios";

const API = process.env.REACT_APP_BACKEND_URL;

const CATEGORIES = [
  { value: "all", label: "All" },
  { value: "market-insight", label: "Market Insights" },
  { value: "whitepaper", label: "Whitepapers" },
  { value: "insight", label: "Insights" },
];

export default function InsightsPage() {
  const [blogs, setBlogs] = useState([]);
  const [filter, setFilter] = useState("all");
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    axios.get(`${API}/api/blogs?published_only=true`)
      .then(r => setBlogs(r.data))
      .catch(() => setBlogs([]))
      .finally(() => setLoading(false));
  }, []);

  const filtered = filter === "all" ? blogs : blogs.filter(b => b.category === filter);

  return (
    <div data-testid="insights-page">
      {/* Hero */}
      <section className="pt-32 pb-20 md:pt-40 md:pb-28 bg-navy-900">
        <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-16">
          <p className="uppercase tracking-[0.2em] text-xs font-semibold text-gold-500 mb-4 font-sans">Thought Leadership</p>
          <h1 className="font-serif text-4xl sm:text-5xl lg:text-6xl text-white font-light tracking-tight max-w-3xl">
            Insights that drive informed decisions.
          </h1>
          <p className="text-white/60 font-sans text-base md:text-lg mt-6 max-w-2xl leading-relaxed">
            Market intelligence, research papers, and expert perspectives from the Capstone advisory team.
          </p>
        </div>
      </section>

      {/* Filters + Content */}
      <section className="py-24 md:py-32">
        <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-16">
          {/* Filter tabs */}
          <div className="flex flex-wrap gap-2 mb-12">
            {CATEGORIES.map(cat => (
              <button
                key={cat.value}
                data-testid={`filter-${cat.value}`}
                onClick={() => setFilter(cat.value)}
                className={`px-5 py-2 text-sm font-sans font-medium transition-colors rounded-sm ${
                  filter === cat.value
                    ? 'bg-navy-900 text-white'
                    : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                }`}
              >
                {cat.label}
              </button>
            ))}
          </div>

          {loading ? (
            <div className="text-center py-20">
              <div className="w-8 h-8 border-2 border-gold-500 border-t-transparent rounded-full animate-spin mx-auto" />
            </div>
          ) : filtered.length === 0 ? (
            <p className="text-gray-400 font-sans text-center py-20">No insights published yet. Check back soon.</p>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-0 border-t border-l border-gray-200">
              {filtered.map((blog) => (
                <Link
                  key={blog.id}
                  to={`/insights/${blog.slug}`}
                  data-testid={`blog-card-${blog.slug}`}
                  className="group p-8 border-b border-r border-gray-200 hover:bg-gray-50 transition-colors"
                >
                  <span className="text-xs font-sans text-gold-500 uppercase tracking-[0.15em]">
                    {blog.category?.replace("-", " ")}
                  </span>
                  <h3 className="font-serif text-xl text-navy-900 mt-3 mb-3 group-hover:text-gold-600 transition-colors leading-snug">
                    {blog.title}
                  </h3>
                  <p className="text-gray-500 font-sans text-sm leading-relaxed mb-4 line-clamp-3">
                    {blog.excerpt}
                  </p>
                  <div className="flex items-center gap-4 text-xs text-gray-400 font-sans">
                    {blog.author && (
                      <span className="flex items-center gap-1"><User className="w-3 h-3" /> {blog.author}</span>
                    )}
                    <span className="flex items-center gap-1">
                      <Clock className="w-3 h-3" /> {new Date(blog.created_at).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" })}
                    </span>
                  </div>
                </Link>
              ))}
            </div>
          )}
        </div>
      </section>
    </div>
  );
}

export function BlogDetail() {
  const [blog, setBlog] = useState(null);
  const [loading, setLoading] = useState(true);
  const slug = window.location.pathname.split("/").pop();

  useEffect(() => {
    axios.get(`${API}/api/blogs/${slug}`)
      .then(r => setBlog(r.data))
      .catch(() => setBlog(null))
      .finally(() => setLoading(false));
  }, [slug]);

  if (loading) return <div className="pt-40 text-center"><div className="w-8 h-8 border-2 border-gold-500 border-t-transparent rounded-full animate-spin mx-auto" /></div>;
  if (!blog) return <div className="pt-40 text-center"><h1 className="font-serif text-3xl text-navy-900">Article not found</h1><Link to="/insights" className="text-gold-500 font-sans mt-4 inline-block">Back to Insights</Link></div>;

  return (
    <div data-testid="blog-detail-page">
      <section className="pt-32 pb-16 md:pt-40 md:pb-20 bg-navy-900">
        <div className="max-w-4xl mx-auto px-6 md:px-12">
          <Link to="/insights" className="text-white/50 hover:text-white font-sans text-sm mb-6 inline-block transition-colors">&larr; All Insights</Link>
          <span className="block text-xs font-sans text-gold-500 uppercase tracking-[0.15em] mb-4">{blog.category?.replace("-", " ")}</span>
          <h1 className="font-serif text-3xl sm:text-4xl lg:text-5xl text-white font-light tracking-tight leading-tight">{blog.title}</h1>
          <div className="flex items-center gap-4 text-sm text-white/50 font-sans mt-6">
            {blog.author && <span>By {blog.author}</span>}
            <span>{new Date(blog.created_at).toLocaleDateString("en-US", { month: "long", day: "numeric", year: "numeric" })}</span>
          </div>
        </div>
      </section>
      <section className="py-16 md:py-24">
        <div className="max-w-4xl mx-auto px-6 md:px-12">
          <div className="prose prose-lg max-w-none text-gray-600 font-sans leading-relaxed whitespace-pre-line">{blog.content}</div>
        </div>
      </section>
    </div>
  );
}
